import React, { FunctionComponent, memo, useEffect, useState } from "react";
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useFieldArray,
  Controller,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Select from "../../../../Components/Select/AutoComplete";
import { AutocompleteOption } from "../../../../Components/Select/ISelectProps";
import { useAppDispatch, useAppSelector } from "../../../../Redux/Store/Hooks";
import { IClassSelectionMapping } from "./IClassSelection";
import { MultiInputDateRangeField } from "@mui/x-date-pickers-pro";
import {
  Box,
  Button,
  IconButton,
  Switch,
  Typography,
  TextField,
  Chip,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import {
  DateCalendar,
  LocalizationProvider,
  PickersDay,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import utc from "dayjs/plugin/utc";
import customParseFormat from "dayjs/plugin/customParseFormat";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";

import { ClassSelectionCreate_Update } from "../../../../Redux/Slices/Booklet/Admin/ClassSelectionMaster/ClassSelectionMasterCreate_Update";
import { tutorsRead } from "../../../../Redux/Slices/Booklet/Admin/Tutors/TutorsRead";
import { ClassSelectionSubjectRead } from "../../../../Redux/Slices/Booklet/Admin/ClassSelectionMaster/ClassSelectionSubjectRead";
import { centreMasterRead } from "../../../../Redux/Slices/Booklet/Admin/CentreMaster/CentreMatserRead";
import MultiDateSelect from "../../../../Components/Select/MultipleDatePicker";

dayjs.extend(utc);
dayjs.extend(customParseFormat);

interface ClassSelectionFormProps {
  formRef: React.RefObject<HTMLFormElement>;
  initialValues: Partial<IClassSelectionMapping>;
  setInitialValues: React.Dispatch<
    React.SetStateAction<Partial<IClassSelectionMapping>>
  >;
  isEdit: boolean;
}

const ClassSelectionForm: FunctionComponent<ClassSelectionFormProps> = ({
  formRef,
  initialValues,
  setInitialValues,
  isEdit,
}) => {
  const dispatch = useAppDispatch();

  const { data: tutorList } = useAppSelector((s) => s.TutorsReadSlice);
  const { data: subjectList } = useAppSelector(
    (s) => s.ClassSelectionSubjectRead
  );
  const { data: centerList } = useAppSelector((s) => s.CentreMasterReadSlice);

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [originalDates, setOriginalDates] = useState<any[]>([]);

  const schema = z.object({
    class_selection_master_id: z.string(),
    tutor_id: z
      .number({
        required_error: "Please select a tutor",
      })
      .refine((val) => val > 0, { message: "Please select a tutor" }),
    center_id: z.string().min(1, "Please select a center"),
    status: z.boolean(),
    is_delete: z.boolean().default(false),

    selections: z
      .array(
        z.object({
          dates: z
            .array(
              z.object({
                csmd_id: z.string().optional(),
                date: z.string(),
                day: z.string(),
              })
            )
            .min(1, "Please select at least one date"),
          subjects: z
            .array(z.number().min(1))
            .min(1, "Please select at least one subject"),
        })
      )
      .min(1, "Please add at least one selection"),
  });

  // FIX: Handle mapping from initialValues.dates (for edit) or selections, default to empty array
  const parsedInitialSelections = (() => {
    const init: any = initialValues;

    let selectionsData: any[] = [];
    if (init?.selections) {
      selectionsData = init.selections;
    } else if (init?.dates) {
      selectionsData = init.dates;
    }

    if (selectionsData.length > 0) {
      return selectionsData.map((s: any) => ({
        dates: s.dates.map((d: any) => ({
          csmd_id: d.csmd_id,
          date: dayjs(d.date).format("YYYY-MM-DD"),
          day: d.day,
        })),
        subjects: s.subjects.map((x: any) => x.subject_id),
      }));
    }

    return [];
  })();

  const methods = useForm<any>({
    defaultValues: {
      class_selection_master_id:
        initialValues?.class_selection_master_id ?? "0",

      tutor_id: initialValues?.tutor_id ?? 0,
      center_id: initialValues?.center_id ?? "",
      status: initialValues?.status ?? true,
      is_delete: false,
      selections: parsedInitialSelections ?? [],
      new_selection_subjects: [],
      new_selection_dates: [],
      editing_selection: {
        subjects: [],
        dates: [],
      },
    },
    resolver: zodResolver(schema),
    mode: "onChange",
  });

  console.log(initialValues, "initialValues");

  const { control, watch, setValue, getValues, handleSubmit } = methods;

  const {
    fields,
    append,
    remove,
    update: updateFieldArray,
  } = useFieldArray({
    control,
    name: "selections",
  });

  const tutorOptions =
    tutorList?.data?.map((t: any) => ({
      label: t.username,
      value: t.id,
    })) ?? [];

  const subjectOptions =
    subjectList?.data?.map((s: any) => ({
      label: s.name,
      value: s.id,
    })) ?? [];

  const centerOptions =
    centerList?.data?.map((c: any) => ({
      label: `${c.center_name} (${c.center_id})`,
      value: c.center_id,
    })) ?? [];

  useEffect(() => {
    dispatch(tutorsRead({ role: "TUTOR" }));
    dispatch(ClassSelectionSubjectRead());
    dispatch(centreMasterRead());
  }, []);

  const onSubmit: SubmitHandler<any> = (values) => {
    const payload = {
      class_selection_master_id: values.class_selection_master_id ?? "0",
      tutor_id: values.tutor_id,
      center_id: values.center_id,
      status: values.status,
      is_delete: false,

      dates: values.selections.map((selection: any) => ({
        dates: selection.dates.map((d: any) => ({
          csmd_id: d.csmd_id || undefined,
          date: dayjs(d.date).utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
          day: d.day,
        })),
        subjects: selection.subjects.map((id: number) => ({ subject_id: id })),
      })),
    };

    dispatch(ClassSelectionCreate_Update(payload));
  };

  const handleAddDate = () => {
    // This function seems unused in the current code; consider removing if not needed
    // or implement if required for single date addition
  };

  const handleRemoveDateFromSelection = (
    selectionIndex: number,
    dateStr: string
  ) => {
    const current = methods.getValues(`selections.${selectionIndex}.dates`);
    const updated = current.filter((d: any) => d.date !== dateStr);
    setValue(`selections.${selectionIndex}.dates`, updated, {
      shouldValidate: true,
    });
  };

  const handleRemoveSubjectFromSelection = (
    selectionIndex: number,
    subjectId: number
  ) => {
    const current = methods.getValues(`selections.${selectionIndex}.subjects`);
    const updated = current.filter((id: number) => id !== subjectId);
    setValue(`selections.${selectionIndex}.subjects`, updated, {
      shouldValidate: true,
    });
  };

  const leftPanelStyle: React.CSSProperties = {
    position: "sticky",
    top: "0",
    height: "70vh",
    overflowY: "auto",
    padding: "24px",
    backgroundColor: "#ffffff",
    borderRight: "1px solid #e0e0e0",
  };

  const rightPanelStyle: React.CSSProperties = {
    height: "70vh",
    overflowY: "auto",
    padding: "24px",
  };

  const selectionBoxStyle: React.CSSProperties = {
    backgroundColor: "#f8f9fa",
    padding: "20px",
    borderRadius: "12px",
    marginBottom: "16px",
    border: "1px solid #e9ecef",
  };

  const watchedSelections = watch("selections");

  // FIX: Controller for new_selection_subjects and new_selection_dates
  useEffect(() => {
    // Ensure new selection fields are registered
    setValue("new_selection_subjects", [], { shouldValidate: false });
    setValue("new_selection_dates", [], { shouldValidate: false });
    setValue("editing_selection.subjects", [], { shouldValidate: false });
    setValue("editing_selection.dates", [], { shouldValidate: false });
  }, [setValue]);

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setOriginalDates([]);
    // FIX: Explicitly clear nested fields instead of setting parent to undefined
    setValue("editing_selection.subjects", [], { shouldValidate: false });
    setValue("editing_selection.dates", [], { shouldValidate: false });
    setValue("new_selection_subjects", [], { shouldValidate: false });
    setValue("new_selection_dates", [], { shouldValidate: false });
  };

  const isEditing = editingIndex !== null;

  useEffect(() => {
    methods.register("editing_selection.subjects");
    methods.register("editing_selection.dates");
    methods.register("new_selection_subjects");
    methods.register("new_selection_dates");
  }, [methods]);

  useEffect(() => {
  if (editingIndex === null) return;

  const selection = getValues(`selections.${editingIndex}`);

  if (!selection) return;

  // 🔥 SET AFTER CONTROLLERS EXIST
  setValue(
    "editing_selection.subjects",
    [...(selection.subjects ?? [])],
    { shouldDirty: false }
  );

  setValue(
    "editing_selection.dates",
    [...(selection.dates ?? [])],
    { shouldDirty: false }
  );
}, [editingIndex]);


  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <FormProvider {...methods}>
        <form
          ref={formRef}
          onSubmit={handleSubmit(onSubmit)}
          style={{
            display: "grid",
            gridTemplateColumns: "420px 1fr",
            gap: "0",
            height: "100%",
            maxWidth: "100%",
            width: "100%",
          }}
        >
          {/* LEFT PANEL */}
          <div style={leftPanelStyle}>
            {/* Tutor */}
            <Box sx={{ mb: 2.5 }}>
              <Controller
                name="tutor_id"
                control={control}
                render={({ field, fieldState }) => (
                  <Select
                    name="tutor_id"
                    label="Tutor *"
                    options={tutorOptions}
                    value={tutorOptions.find(
                      (o: any) => o.value === field.value
                    )}
                    onChange={(_, v) =>
                      field.onChange((v as AutocompleteOption)?.value ?? 0)
                    }
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Box>

            {/* Center */}
            <Box sx={{ mb: 2.5 }}>
              <Controller
                name="center_id"
                control={control}
                render={({ field, fieldState }) => (
                  <Select
                    name="center_id"
                    label="Center *"
                    options={centerOptions}
                    value={centerOptions.find(
                      (o: any) => o.value === field.value
                    )}
                    onChange={(_, v) =>
                      field.onChange((v as AutocompleteOption)?.value ?? "")
                    }
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Box>

            {/* Subjects for new/editing */}
            <Box sx={{ mb: 2.5 }}>
              <Controller
                name={
                  isEditing
                    ? "editing_selection.subjects"
                    : "new_selection_subjects"
                }
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <Select
                    name="subjects"
                    label="Subjects (Multi Select) *"
                    options={subjectOptions}
                    value={subjectOptions.filter((x: any) =>
                      value?.includes(x.value)
                    )}
                    multiple
                    onChange={(_, v) =>
                      onChange(
                        (v as AutocompleteOption[])?.map((x) => x.value) ?? []
                      )
                    }
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Box>

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Select Dates *
              </Typography>

              <Controller
                name={
                  isEditing ? "editing_selection.dates" : "new_selection_dates"
                }
                control={control}
                render={({ field, fieldState }) => {
                  const pickerValue = (field.value || []).map((d: any) =>
                    dayjs(d.date)
                  );
                  const handleDateChange = (val: Dayjs[]) => {
                    let newDates: any[];
                    if (isEditing && originalDates.length > 0) {
                      newDates = val.map((date: Dayjs) => {
                        const dateStr = date.format("YYYY-MM-DD");
                        const orig = originalDates.find(
                          (od: any) => od.date === dateStr
                        );
                        return {
                          csmd_id: orig?.csmd_id || undefined,
                          date: dateStr,
                          day: date.format("dddd"),
                        };
                      });
                    } else {
                      newDates = val.map((date: Dayjs) => ({
                        csmd_id: undefined,
                        date: date.format("YYYY-MM-DD"),
                        day: date.format("dddd"),
                      }));
                    }
                    field.onChange(newDates);
                  };

                  return (
                    <MultiDateSelect
                      label="Select Dates *"
                      value={pickerValue}
                      onChange={handleDateChange}
                    />
                  );
                }}
              />
            </Box>

            {/* Status */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                mb: 3,
                padding: 1.5,
                borderRadius: 2,
              }}
            >
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <>
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                    <Typography variant="body2">Active Status</Typography>
                  </>
                )}
              />
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: "flex", gap: 1.5 }}>
              {isEditing ? (
                <>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleCancelEdit}
                  >
                    Cancel
                  </Button>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      // FIX: Explicitly get nested values for validation and update
                      const editedSubjects =
                        getValues("editing_selection.subjects") ?? [];
                      const editedDates =
                        getValues("editing_selection.dates") ?? [];
                      if (!editedDates.length || !editedSubjects.length) {
                        alert("Please select dates and subjects for editing.");
                        return;
                      }

                      updateFieldArray(editingIndex, {
                        dates: editedDates,
                        subjects: editedSubjects,
                      });
                      handleCancelEdit();
                    }}
                    sx={{
                      color:"#ffffff",
                      backgroundColor: "#009ddf",
                      "&:hover": {
                        backgroundColor: "#0088cc",
                      },
                    }}
                  >
                    Save Changes
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => {
                      setValue("new_selection_dates", []);
                      setValue("new_selection_subjects", []);
                    }}
                  >
                    Reset
                  </Button>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      const newDates = getValues("new_selection_dates") ?? [];
                      const newSubjects =
                        getValues("new_selection_subjects") ?? [];

                      if (!newDates.length || !newSubjects.length) {
                        alert("Please select dates and subjects.");
                        return;
                      }

                      append({
                        dates: [...newDates],
                        subjects: [...newSubjects],
                      });

                      // ✅ FORCE clear with new references
                      setValue("new_selection_dates", [], {
                        shouldDirty: false,
                        shouldTouch: false,
                      });

                      setValue("new_selection_subjects", [], {
                        shouldDirty: false,
                        shouldTouch: false,
                      });
                    }}
                    sx={{
                       color:"#ffffff",
                      backgroundColor: "#009ddf",
                      "&:hover": {
                        backgroundColor: "#0088cc",
                      },
                    }}
                  >
                    Add Selection
                  </Button>
                </>
              )}
            </Box>
          </div>

          {/* RIGHT PANEL */}
          <div style={rightPanelStyle}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Selections ({fields.length})
              </Typography>
            </Box>
            {methods.formState.errors?.selections && (
              <Box>
                <Typography
                  color="error"
                  variant="body2"
                  sx={{
                    mb: 2,
                    mt: 2,
                    fontSize: "13px",
                    fontWeight: "400",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  {String(
                    methods.formState.errors.selections.message ??
                      "Please add at least one selection"
                  )}
                </Typography>
              </Box>
            )}

            {fields.length === 0 && (
              <Box
                sx={{
                  textAlign: "center",
                  py: 6,
                  color: "#999",
                }}
              >
                <Typography>No selections added yet</Typography>
                <Typography variant="caption">
                  Use the form on the left to add selections
                </Typography>
              </Box>
            )}

            {fields.map((field, index) => {
              const selDates = watchedSelections?.[index]?.dates ?? [];
              const selSubjects = watchedSelections?.[index]?.subjects ?? [];

              return (
                <div key={field.id} style={selectionBoxStyle}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Selection {index + 1}
                    </Typography>

                    <Box>
                      <IconButton
                        size="small"
                        onClick={() => {
                          const currentSelection = getValues(
                            `selections.${index}`
                          );

                          setEditingIndex(index);

                          setValue(
                            "editing_selection.subjects",
                            [...(currentSelection?.subjects ?? [])],
                            { shouldDirty: false }
                          );

                          setValue(
                            "editing_selection.dates",
                            [...(currentSelection?.dates ?? [])],
                            { shouldDirty: false }
                          );

                          setOriginalDates(currentSelection?.dates ?? []);
                        }}
                        sx={{ mr: 0.5 }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>

                      <IconButton size="small" onClick={() => remove(index)}>
                        <DeleteOutlineIcon fontSize="small" color="error" />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Dates */}
                  <Box sx={{ mb: "12px" }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 600 }}
                    >
                      Dates ({selDates.length})
                    </Typography>

                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {selDates.length ? (
                        selDates.map((d: any, i: number) => (
                          <Chip
                            key={i}
                            label={dayjs(d.date).format("DD MMM YYYY")}
                            onDelete={
                              editingIndex === index
                                ? () =>
                                    handleRemoveDateFromSelection(index, d.date)
                                : undefined
                            }
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ))
                      ) : (
                        <Typography variant="caption" sx={{ color: "#999" }}>
                          No dates selected
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Subjects */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 600 }}
                    >
                      Subjects ({selSubjects.length})
                    </Typography>

                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {selSubjects.length ? (
                        selSubjects.map((sId: number, i: number) => {
                          const subject = subjectOptions.find(
                            (x: any) => x.value === sId
                          );
                          return (
                            <Chip
                              key={i}
                              label={subject?.label}
                              onDelete={
                                editingIndex === index
                                  ? () =>
                                      handleRemoveSubjectFromSelection(
                                        index,
                                        sId
                                      )
                                  : undefined
                              }
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          );
                        })
                      ) : (
                        <Typography variant="caption" sx={{ color: "#999" }}>
                          No subjects selected
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </div>
              );
            })}
          </div>
        </form>
      </FormProvider>
    </LocalizationProvider>
  );
};

export default memo(ClassSelectionForm);
